import { Children, forwardRef, useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import clsx from 'clsx';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { amountToInput, parseAmountInput } from '@/lib/format';

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
  htmlFor,
}: {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  return (
    <div className={clsx('min-w-0', className)}>
      {label && (
        <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-700">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}
      {children}
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input(
  { className, invalid, ...rest },
  ref,
) {
  return <input ref={ref} className={clsx('input-base', invalid && 'border-red-400 focus:border-red-500 focus:ring-red-200', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { className, invalid, rows = 3, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={clsx('input-base', invalid && 'border-red-400', className)} {...rest} />;
});

export interface SelectOption {
  value: string;
  label: string;
}

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { options: SelectOption[]; placeholder?: string; invalid?: boolean }
>(function Select({ options, placeholder, className, invalid, ...rest }, ref) {
  return (
    <select ref={ref} className={clsx('input-base pr-8', invalid && 'border-red-400', className)} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
});

export function Checkbox({ label, checked, onChange, disabled, hint }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string }) {
  const id = useId();
  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-300"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={id} className="text-sm text-slate-700">
        {label}
        {hint && <span className="block text-xs text-slate-500">{hint}</span>}
      </label>
    </div>
  );
}

/**
 * Betragseingabe im deutschen Format ("1.234,56"). value/onChange arbeiten mit dem API-Format "1234.56".
 */
export function MoneyInput({
  value,
  onChange,
  invalid,
  placeholder = '0,00',
  disabled,
  id,
  allowNegative,
}: {
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  invalid?: boolean;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  allowNegative?: boolean;
}) {
  const [text, setText] = useState(amountToInput(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(amountToInput(value));
  }, [value, focused]);
  const parsed = text.trim() ? parseAmountInput(text) : null;
  const bad = (text.trim() !== '' && parsed === null) || (!allowNegative && parsed?.startsWith('-'));
  return (
    <div className="relative">
      <input
        id={id}
        inputMode="decimal"
        className={clsx('input-base pr-8 text-right tabular', (invalid || bad) && 'border-red-400')}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          if (parsed !== null) setText(amountToInput(parsed));
        }}
        onChange={(e) => {
          setText(e.target.value);
          const p = e.target.value.trim() ? parseAmountInput(e.target.value) : null;
          onChange(p);
        }}
      />
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-400">€</span>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Suchen …', className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={clsx('relative', className)}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input className="input-base pl-8 pr-8" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      {value && (
        <button type="button" aria-label="Suche leeren" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => onChange('')}>
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/** Kompakte Filterleiste über Tabellen */
/**
 * Filterleiste. Auf dem Smartphone bleibt nur das erste Feld (meist die Suche) sichtbar;
 * die übrigen Filter klappen über „Filter“ auf – mit Anzahl der gesetzten Filter.
 */
export function FilterBar({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const count = Children.toArray(children).length;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || count < 2) return;
    const extra = [...el.children].slice(1).filter((c) => !(c as HTMLElement).dataset.filterToggle);
    let n = 0;
    for (const c of extra) {
      const fields = c.querySelectorAll<HTMLInputElement | HTMLSelectElement>('select, input');
      if ([...fields].some((f) => (f instanceof HTMLInputElement && (f.type === 'checkbox' || f.type === 'radio') ? f.checked : f.value !== ''))) n++;
    }
    setActive(n);
  });
  const collapsible = count > 1;
  return (
    <div className="flex flex-col gap-3 border-b border-slate-200 p-3 sm:flex-row sm:flex-wrap sm:items-end">
      <div
        ref={ref}
        className={clsx(
          'grid flex-1 grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 gap-y-3 sm:flex sm:flex-row sm:flex-wrap sm:items-end sm:gap-3 [&>*]:sm:w-auto [&>*]:sm:min-w-[160px]',
          '[&>*:nth-child(n+2):not([data-filter-toggle])]:col-span-2 [&>*:nth-child(n+2):not([data-filter-toggle])]:order-2',
          collapsible && !open && 'max-sm:[&>*:nth-child(n+2):not([data-filter-toggle])]:hidden',
          !collapsible && '[&>*:first-child]:col-span-2',
        )}
      >
        {children}
        {collapsible && (
          <button
            type="button"
            data-filter-toggle="1"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className={clsx(
              'order-1 flex h-[42px] items-center gap-1.5 rounded-md border px-3 text-sm font-medium sm:hidden',
              open || active ? 'border-brand-300 bg-brand-50 text-brand-800' : 'border-slate-300 bg-white text-slate-600',
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filter
            {active > 0 && <span className="rounded-full bg-brand-700 px-1.5 text-[11px] font-semibold text-white">{active}</span>}
          </button>
        )}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}
